/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: EmployeeItem.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-EMPLOYEES
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * EmployeeItem - Строка сотрудника с аватаром, ролью и действиями.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-14 - UX: аватар, цветная роль, загрузка, hover-строка, отсутствия.
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракт компонента и парный END_METHOD.
 * END_CHANGE_SUMMARY
 */

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import { alpha, useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import ProjectAvatar from '../../../components/ProjectAvatar';
import RoleWorkloadBar from '../../../components/RoleWorkloadBar';
import { getColorByName } from '../../../utils/projectColors';
import { createInteractiveSurfaceTransition } from '../../../theme/appTheme';

/* START_METHOD_EmployeeItem */
/* START_CONTRACT: EmployeeItem
 * PURPOSE: Отобразить строку сотрудника с ролью и действиями редактирования/удаления.
 * INPUTS: { employee: Employee, onEdit: (e)=>void, onDelete: (e)=>void, isLast?: boolean }.
 * OUTPUTS: { JSX.Element - строка }.
 * SIDE_EFFECTS: нет (делегирует onEdit/onDelete).
 * LINKS: M-FE-EMPLOYEES
 * END_CONTRACT: EmployeeItem
 */
export default function EmployeeItem({ employee, onEdit, onDelete, isLast = false }) {
  const theme = useTheme();
  const roleColor = getColorByName(employee.projectRoleName || employee.name);
  const absenceCount = employee.absences?.length ?? 0;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        px: 2,
        borderBottom: isLast ? 0 : 1,
        borderColor: 'divider',
        transition: createInteractiveSurfaceTransition(theme, ['background-color']),
        '&:hover': {
          bgcolor: alpha(theme.palette.primary.main, 0.03),
          '& .employee-row-actions': {
            opacity: 1,
          },
        },
        '@media (hover: none)': {
          '& .employee-row-actions': {
            opacity: 1,
          },
        },
      }}
    >
      <ProjectAvatar name={employee.name} size={36} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography
            variant="body2"
            fontWeight={600}
            noWrap
            sx={{ letterSpacing: '-0.01em' }}
          >
            {employee.name}
          </Typography>
          {employee.projectRoleName && (
            <Chip
              label={employee.projectRoleName}
              size="small"
              sx={{
                height: 22,
                fontWeight: 600,
                fontSize: '0.7rem',
                bgcolor: roleColor,
                color: '#fff',
                '& .MuiChip-label': { px: 1 },
              }}
            />
          )}
          <RoleWorkloadBar
            value={employee.projectRoleWorkloadPercent}
            accentColor={roleColor}
            size="md"
          />
        </Box>
        {absenceCount > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <EventBusyOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.secondary">
              {absenceCount === 1
                ? '1 отсутствие'
                : `${absenceCount} отсутствия`}
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        className="employee-row-actions"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          flexShrink: 0,
          opacity: { xs: 1, sm: 0.55 },
          transition: createInteractiveSurfaceTransition(theme, ['opacity']),
        }}
      >
        <Tooltip title="Редактировать">
          <IconButton
            size="small"
            aria-label={`Редактировать ${employee.name}`}
            onClick={() => onEdit(employee)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Удалить">
          <IconButton
            size="small"
            color="error"
            aria-label={`Удалить ${employee.name}`}
            onClick={() => onDelete(employee)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
/* END_METHOD_EmployeeItem */
